import React, { useEffect } from "react";
import {
  LockClosedIcon,
  XMarkIcon,
  FilmIcon,
  BookOpenIcon,
  MusicalNoteIcon,
  ShoppingBagIcon,
  HeartIcon,
  CheckCircleIcon,
  ArrowRightOnRectangleIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

export default function InfoModal({
  handelModal,
  onClose,
  onLoginClick,
  onSignupClick,
  title,
  message,
  actionType = "general",
  itemTitle,
  dark = true,
}) {
  const closeModal = onClose || handelModal;

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && closeModal) {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal]);

  // Determine dynamic title and icon based on actionType
  const getActionDetails = () => {
    switch (actionType) {
      case "watch":
        return {
          icon: <FilmIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Sign In to Watch",
          defaultMsg: itemTitle
            ? `Please sign in to stream "${itemTitle}". Free accounts get instant access!`
            : "Please sign in to stream this video. Free accounts get instant access to our catalog!",
        };
      case "read":
        return {
          icon: <BookOpenIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Sign In to Read",
          defaultMsg: itemTitle
            ? `Please sign in to read "${itemTitle}". Free accounts enjoy immediate reading access!`
            : "Please sign in to read this e-book. Free accounts enjoy immediate reading access!",
        };
      case "listen":
        return {
          icon: <MusicalNoteIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Sign In to Listen",
          defaultMsg: itemTitle
            ? `Please sign in to play "${itemTitle}". Free accounts enjoy streaming audio & music!`
            : "Please sign in to listen to this audio. Free accounts enjoy streaming audio and curated playlists!",
        };
      case "buy":
        return {
          icon: <ShoppingBagIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Sign In to Purchase",
          defaultMsg: itemTitle
            ? `Please sign in to purchase or rent "${itemTitle}".`
            : "Please sign in to purchase or rent this item securely.",
        };
      case "wishlist":
        return {
          icon: <HeartIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Save to Wishlist",
          defaultMsg: "Please sign in to add your favorite titles to your personal wishlist.",
        };
      default:
        return {
          icon: <LockClosedIcon className="w-7 h-7 text-secondary" />,
          defaultTitle: "Sign In Required",
          defaultMsg:
            "Please sign in or create a free account to unlock BAB Digital media and releases.",
        };
    }
  };

  const actionDetails = getActionDetails();
  const displayTitle = title || actionDetails.defaultTitle;
  const displayMsg = message || actionDetails.defaultMsg;

  return (
    <div
      onClick={closeModal}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md transition-all duration-300 animate-fadeIn"
      role="dialog"
      aria-modal="true"
    >
      {/* Modal Dialog Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 transition-all duration-300 transform border ${
          dark
            ? "bg-zinc-950/95 border-zinc-800/90 text-white shadow-black/80"
            : "bg-white border-zinc-200 text-zinc-900 shadow-xl"
        }`}
      >
        {/* Subtle Ambient Decorative Glow */}
        <div className="absolute -top-20 -right-20 w-52 h-52 bg-secondary/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-52 h-52 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        {closeModal && (
          <button
            onClick={closeModal}
            aria-label="Close dialog"
            className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white bg-zinc-900/60 hover:bg-zinc-800 border border-zinc-800/80 transition-colors z-10 active:scale-95"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}

        {/* Illuminated Icon Badge */}
        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-secondary/25 via-secondary/10 to-amber-500/20 border border-secondary/40 flex items-center justify-center shadow-lg shadow-secondary/15 mb-4">
            {actionDetails.icon}
          </div>

          {/* Heading */}
          <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-2">
            {displayTitle}
          </h3>

          {/* Description */}
          <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-6 max-w-sm">
            {displayMsg}
          </p>

          {/* Value Proposition Highlights */}
          <div className="w-full bg-zinc-900/60 rounded-2xl p-3.5 mb-6 border border-zinc-800/70 text-left space-y-2">
            <div className="flex items-center gap-2.5 text-xs text-zinc-300">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Instant streaming for all free catalog titles</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs text-zinc-300">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Continuous reading & playlist sync across devices</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs text-zinc-300">
              <CheckCircleIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>100% free account creation in under a minute</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="w-full flex flex-col gap-2.5">
            <button
              onClick={onLoginClick}
              className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/30 transition-all duration-200 transform hover:scale-[1.01] active:scale-95 flex items-center justify-center gap-2"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Sign In to Your Account</span>
            </button>

            {onSignupClick ? (
              <button
                onClick={onSignupClick}
                className="w-full py-3 px-6 rounded-xl font-bold text-sm bg-zinc-900 hover:bg-zinc-800 text-zinc-200 hover:text-white border border-zinc-700/80 transition-all duration-200 transform hover:scale-[1.01] active:scale-95 flex items-center justify-center gap-2"
              >
                <UserPlusIcon className="w-4 h-4 text-secondary" />
                <span>Create a Free Account</span>
              </button>
            ) : (
              <div className="text-center pt-1 text-xs text-zinc-500">
                Don&apos;t have an account yet?{" "}
                <button
                  onClick={onLoginClick}
                  className="text-secondary font-bold hover:underline"
                >
                  Get started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
